import { useState, useEffect, useCallback } from 'react';

export interface UseCountdownTimerOptions {
  /**
   * Initial countdown duration in seconds
   */
  initialDuration?: number;
  /**
   * Whether the timer should start automatically
   */
  autoStart?: boolean;
  /**
   * Callback function called when countdown reaches zero
   */
  onComplete?: () => void;
}

export interface UseCountdownTimerProps {
  /**
   * Duration for the countdown timer in seconds
   */
  duration?: number;
  /**
   * Whether the timer should start automatically
   */
  autoStart?: boolean;
  /**
   * Callback function called when countdown reaches zero
   */
  onComplete?: () => void;
}

export interface UseCountdownTimerReturn {
  /**
   * Current countdown value in seconds
   */
  countdown: number;
  /**
   * Whether the timer is currently running
   */
  isRunning: boolean;
  /**
   * Whether the countdown has reached zero
   */
  isComplete: boolean;
  /**
   * Start the countdown timer
   */
  start: (duration?: number) => void;
  /**
   * Stop the countdown timer
   */
  stop: () => void;
  /**
   * Reset the countdown timer to initial duration
   */
  reset: () => void;
  /**
   * Restart the countdown timer with current or provided duration
   */
  restart: (duration?: number) => void;
}

/**
 * Custom hook for managing countdown timers
 * 
 * @param duration Duration for the countdown timer in seconds
 * @param options Additional configuration options
 * @returns Object containing countdown state and control functions
 * 
 * @example
 * ```tsx
 * const { countdown, isRunning, start, stop } = useCountdownTimer(30, {
 *   autoStart: false,
 *   onComplete: () => console.log('Timer completed!')
 * });
 * 
 * return (
 *   <button 
 *     disabled={isRunning} 
 *     onClick={() => start()}
 *   >
 *     {isRunning ? `Resend in ${countdown}s` : 'Resend'}
 *   </button>
 * );
 * ```
 */
export function useCountdownTimer(
  duration: number = 0,
  options: UseCountdownTimerOptions = {}
): UseCountdownTimerReturn {
  const { autoStart = false, onComplete } = options;
  
  const [countdown, setCountdown] = useState(duration);
  const [isRunning, setIsRunning] = useState(false);

  const isComplete = countdown === 0;

  const start = useCallback((customDuration?: number) => {
    const startDuration = customDuration ?? duration;
    
    setCountdown(startDuration);
    setIsRunning(true);
  }, [duration]);

  const stop = useCallback(() => {
    setIsRunning(false);
  }, []);

  const reset = useCallback(() => {
    setCountdown(duration);
    setIsRunning(false);
  }, [duration]);

  const restart = useCallback((customDuration?: number) => {
    const restartDuration = customDuration ?? duration;

    setCountdown(restartDuration);
    setIsRunning(true);
  }, [duration]);

  // Timer effect
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (isRunning && countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            onComplete?.();

            return 0;
          }

          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunning, countdown, onComplete]);

  // Auto-start effect
  useEffect(() => {
    if (autoStart && duration > 0) {
      start();
    }
  }, [autoStart, duration, start]);

  return {
    countdown,
    isRunning,
    isComplete,
    start,
    stop,
    reset,
    restart,
  };
}
